import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, Banner, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";

interface JobLeaveConfirmModalProps {
  message: string;
  confirmLabel?: string;
  tone?: "critical" | "success" | undefined;
  onConfirm: () => Promise<void>;
}

export default function JobLeaveConfirmModal({
  message,
  confirmLabel,
  tone,
  onConfirm,
}: JobLeaveConfirmModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setErrMsg(null);
      await onConfirm();
      closeModal();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || t("error");
      setErrMsg(typeof msg === "string" ? msg : t("error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {errMsg && (
        <Banner tone="critical" onDismiss={() => setErrMsg(null)}>
          {errMsg}
        </Banner>
      )}

      <div className="text-center py-2">
        <Text as="p" variant="bodyMd">
          {message}
        </Text>
      </div>

      <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
        <Button onClick={closeModal} disabled={isLoading}>
          {t("No")}
        </Button>
        <Button
          onClick={handleConfirm}
          loading={isLoading}
          variant="primary"
          tone={tone}
        >
          {confirmLabel || t("Yes")}
        </Button>
      </div>
    </div>
  );
}
